import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Plus,
  List,
  FolderOpen,
  MessageSquare,
  BarChart3,
  Settings,
  CreditCard,
  LogOut,
  ChevronLeft,
  ChevronDown,
  Menu,
  X,
  User,
  Shield,
  Bug,
} from 'lucide-react';
import { LOGO_URL, ICON_URL } from '../../constants';
import { useAuth } from '../../hooks/useAuth';

interface SubMenuItem {
  icon: React.ElementType;
  label: string;
  href: string;
}

interface MenuItem {
  icon: React.ElementType;
  label: string;
  href?: string;
  subItems?: SubMenuItem[];
}

const menuItems: MenuItem[] = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    href: '/dashboard'
  },
  {
    icon: FileText,
    label: 'Formulários',
    subItems: [
      { icon: BarChart3, label: 'Visão geral', href: '/dashboard/forms' },
      { icon: Plus, label: 'Novo', href: '/dashboard/forms/new' },
      { icon: List, label: 'Todos', href: '/dashboard/forms/all' },
      { icon: FolderOpen, label: 'Templates', href: '/dashboard/forms/templates' },
      { icon: MessageSquare, label: 'Respostas', href: '/dashboard/forms/responses' },
    ]
  },
  {
    icon: Settings,
    label: 'Ajustes',
    subItems: [
      { icon: Settings, label: 'Geral', href: '/dashboard/settings' },
    ]
  },
];

/*
 * Z-INDEX HIERARCHY (app-wide):
 * z-0    Background blobs (pointer-events: none)
 * z-10   (reserved — no stacking context on main content)
 * z-30   Mobile sidebar overlay (backdrop)
 * z-40   Sidebar navigation (fixed)
 * z-50   Modals / Dialogs / Overlays / Mobile toggle button
 * z-9999 Crop modal (AvatarUpload) — via Portal on document.body
 * z-[100] Toast notifications
 */

export interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Formulários', 'Ajustes']);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      // Mesmo se signOut falhar, redirecionar para login
    }
    navigate('/auth/login');
  };

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const isMenuActive = (item: MenuItem) => {
    if (item.href) {
      return location.pathname === item.href;
    }
    if (item.subItems) {
      return item.subItems.some(sub => location.pathname.startsWith(sub.href));
    }
    return false;
  };

  const isSubItemActive = (href: string) => {
    if (href === '/dashboard/forms') {
      return location.pathname === '/dashboard/forms';
    }
    if (href === '/dashboard/forms/all') {
      return location.pathname === '/dashboard/forms/all' ||
             location.pathname.match(/^\/dashboard\/forms\/[^/]+\/edit$/);
    }
    if (href === '/dashboard/forms/responses') {
      return location.pathname === '/dashboard/forms/responses' ||
             location.pathname.match(/^\/dashboard\/forms\/[^/]+\/responses/);
    }
    return location.pathname === href;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-neutral-700/50">
        <div className="flex items-center justify-between">
          {isCollapsed ? (
            <img
              src={ICON_URL}
              alt="One Form"
              className="h-8 w-8 cursor-pointer mx-auto object-contain"
              onClick={() => navigate('/dashboard')}
            />
          ) : (
            <img
              src={LOGO_URL}
              alt="One Form"
              className="h-5 w-auto cursor-pointer"
              onClick={() => navigate('/dashboard')}
            />
          )}
          {!isCollapsed && (
            <button
              onClick={onToggle}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors hidden lg:flex"
            >
              <ChevronLeft className="w-4 h-4 transition-transform" />
            </button>
          )}
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors lg:hidden"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {isCollapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors hidden lg:flex mx-auto mt-2"
          >
            <ChevronLeft className="w-4 h-4 transition-transform rotate-180" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isExpanded = expandedMenus.includes(item.label);
          const isActive = isMenuActive(item);

          if (hasSubItems) {
            return (
              <div key={item.label}>
                {/* Parent Menu */}
                <button
                  onClick={() => !isCollapsed && toggleMenu(item.label)}
                  className={`
                    flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all w-full
                    ${isActive
                      ? 'bg-primary-500/10 text-primary-400'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    }
                    ${isCollapsed ? 'justify-center' : 'justify-between'}
                  `}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </div>
                  {!isCollapsed && (
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>

                {/* Sub Items */}
                {!isCollapsed && isExpanded && (
                  <div className="mt-0.5 ml-3 pl-3 border-l border-neutral-700/50 space-y-0.5">
                    {item.subItems?.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = isSubItemActive(subItem.href);

                      return (
                        <NavLink
                          key={subItem.href}
                          to={subItem.href}
                          onClick={onMobileClose}
                          className={`
                            flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all
                            ${isSubActive
                              ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                              : 'text-neutral-500 hover:text-white hover:bg-neutral-700/50'
                            }
                          `}
                        >
                          <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-sm truncate">{subItem.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Regular menu item without sub-items
          return (
            <NavLink
              key={item.href}
              to={item.href!}
              end={item.href === '/dashboard'}
              onClick={onMobileClose}
              className={({ isActive: linkActive }) => `
                flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all
                ${linkActive
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                }
                ${isCollapsed ? 'justify-center' : ''}
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Admin Access (superadmin only) */}
      {user?.is_superadmin && (
        <div className="px-3 pb-1 space-y-1">
          <button
            onClick={() => { navigate('/admin'); onMobileClose(); }}
            className={`
              flex items-center gap-2.5 px-3 py-2 rounded-lg w-full
              text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all border border-red-500/20
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title="Painel Admin"
          >
            <Shield className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span className="text-sm font-medium">Admin</span>}
          </button>
          <button
            onClick={() => {
              window.dispatchEvent(new Event('vf:toggle-debug-panel'));
              onMobileClose();
            }}
            className={`
              flex items-center gap-2.5 px-3 py-2 rounded-lg w-full
              text-neutral-400 hover:text-primary-400 hover:bg-primary-500/10 transition-all border border-neutral-700/40
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title="Debug Panel (Ctrl+Shift+D)"
          >
            <Bug className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span className="text-sm font-medium">Debug</span>}
          </button>
        </div>
      )}

      {/* User & Logout */}
      <div className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-neutral-700/50">
        {user && (
          <button
            onClick={() => navigate('/dashboard/profile')}
            className={`mb-2 w-full cursor-pointer rounded-lg hover:bg-neutral-700/50 transition-colors ${
              isCollapsed ? 'flex justify-center p-2' : 'flex items-center gap-3 px-3 py-2'
            }`}
            title="Meu perfil"
          >
            {/* User Avatar */}
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.full_name || 'Usuário'}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-primary-400" />
              </div>
            )}
            {!isCollapsed && (
              <div className="min-w-0 text-left">
                <p className="text-sm font-medium text-white truncate">{user.full_name || 'Usuário'}</p>
                <p className="text-xs text-neutral-500 truncate">{user.email}</p>
              </div>
            )}
          </button>
        )}
        <button
          onClick={handleLogout}
          className={`
            flex items-center gap-2.5 px-3 py-2 rounded-lg w-full
            text-neutral-400 hover:text-red-400 hover:bg-red-500/10 transition-all
            ${isCollapsed ? 'justify-center' : ''}
          `}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!isCollapsed && <span className="text-sm font-medium">Sair</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile Toggle Button — z-30 so the drawer (z-40) covers it when open */}
      <button
        onClick={() => isMobileOpen ? onMobileClose() : onToggle()}
        className="fixed top-[max(1rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] z-30 p-3 rounded-xl liquid-glass lg:hidden"
      >
        <Menu className="w-5 h-5 text-white" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-40
          bg-neutral-900/95 backdrop-blur-xl border-r border-neutral-700/50
          transition-all duration-300
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};
